import { useCallback, useEffect, useState } from 'react';
import { FiArrowDown, FiArrowUp, FiSearch } from 'react-icons/fi';
import { Modal } from '../../ui';
import { useFinance, usePreferences } from '../../../contexts';
import { api } from '../../../services/api';
import styles from './RecentTransactions.module.css';

interface Transaction {
    id: string;
    description: string;
    amount: number;
    type: 'income' | 'expense';
    category: string;
    date: string;
    icon: string;
    accountName?: string;
}

export function RecentTransactions() {
    const { transactions } = useFinance();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');
    const [searchResults, setSearchResults] = useState<Transaction[]>([]);
    const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
    const [isSearching, setIsSearching] = useState(false);
    const { formatCurrency, t } = usePreferences();

    const formatDate = (dateString: string) => {
        const [datePart] = dateString.split('T');
        const [y, m, d] = datePart.split('-');
        const now = new Date();
        const today = now.toISOString().split('T')[0];
        const yesterday = new Date(now.getTime() - 86400000).toISOString().split('T')[0];

        if (datePart === today) return t('rt.hoje');
        if (datePart === yesterday) return t('rt.ontem');
        return `${d}/${m}/${y}`;
    };

    const getIconClass = (type: string) => {
        return type === 'income' || type === 'RECEITA'
            ? styles.iconIncome
            : styles.iconExpense;
    };

    const doSearch = useCallback(async (page = 1) => {
        setIsSearching(true);
        try {
            const result = await api.searchTransactions({
                q: searchQuery || undefined,
                dataInicio: dataInicio || undefined,
                dataFim: dataFim || undefined,
                page,
                limit: 20,
            });
            setSearchResults(result.data);
            setPagination(result.pagination);
        } catch {
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    }, [searchQuery, dataInicio, dataFim]);

    useEffect(() => {
        if (isModalOpen) {
            doSearch(1);
        }
    }, [isModalOpen]);

    const handleSearch = () => {
        doSearch(1);
    };

    const handlePageChange = (newPage: number) => {
        doSearch(newPage);
    };

    const handleOpenModal = () => {
        setSearchQuery('');
        setDataInicio('');
        setDataFim('');
        setIsModalOpen(true);
    };

    const renderTransactionItem = (transaction: Transaction) => (
        <div key={transaction.id} className={styles.item}>
            <div className={`${styles.icon} ${getIconClass(transaction.type)}`}>
                {transaction.type === 'income'
                    ? <FiArrowUp size={18} />
                    : <FiArrowDown size={18} />}
            </div>
            <div className={styles.details}>
                <div className={styles.description}>{transaction.description}</div>
                <div className={styles.date}>{formatDate(transaction.date)}</div>
            </div>
            <div className={styles.amountSection}>
                <div
                    className={`${styles.amount} ${transaction.type === 'income'
                        ? styles.amountIncome
                        : styles.amountExpense
                        }`}
                >
                    {transaction.type === 'income' ? '+' : '-'}
                    {formatCurrency(transaction.amount)}
                </div>
                <div className={styles.category}>{t(transaction.category)}</div>
            </div>
        </div>
    );

    return (
        <>
            <div className={styles.container}>
                <div className={styles.header}>
                    <h3 className={styles.title}>{t('rt.transacoesRecentes')}</h3>
                    <button className={styles.viewAll} onClick={handleOpenModal}>{t('rt.verTodas')}</button>
                </div>

                <div className={styles.list}>
                    {transactions.slice(0, 5).map(renderTransactionItem)}
                    {transactions.length === 0 && (
                        <p className={styles.emptyText}>{t('rt.nenhumaTransacao')}</p>
                    )}
                </div>
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={t('rt.todasTransacoes')} size="large">
                <div className={styles.modalContent}>
                    <div className={styles.searchBar}>
                        <div className={styles.searchInputWrapper}>
                            <FiSearch className={styles.searchIcon} />
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder={t('rt.buscarPlaceholder')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            />
                        </div>
                        <div className={styles.dateFilters}>
                            <input
                                type="date"
                                className={styles.dateInput}
                                value={dataInicio}
                                onChange={(e) => setDataInicio(e.target.value)}
                            />
                            <span className={styles.dateSep}>{t('common.ate')}</span>
                            <input
                                type="date"
                                className={styles.dateInput}
                                value={dataFim}
                                onChange={(e) => setDataFim(e.target.value)}
                            />
                        </div>
                        <button className={styles.searchBtn} onClick={handleSearch} disabled={isSearching}>
                            {t('common.buscar')}
                        </button>
                    </div>

                    <div className={styles.resultCount}>
                        {t('rt.transacoesEncontradas', { n: String(pagination.total) })}
                    </div>

                    <div className={styles.modalList}>
                        {isSearching ? (
                            <p className={styles.emptyText}>{t('common.buscando')}</p>
                        ) : searchResults.length > 0 ? (
                            searchResults.map(renderTransactionItem)
                        ) : (
                            <p className={styles.emptyText}>{t('rt.nenhumaTrEncontrada')}</p>
                        )}
                    </div>

                    {pagination.totalPages > 1 && (
                        <div className={styles.pagination}>
                            <button
                                className={styles.pageBtn}
                                disabled={pagination.page <= 1}
                                onClick={() => handlePageChange(pagination.page - 1)}
                            >
                                {t('common.anterior')}
                            </button>
                            <span className={styles.pageInfo}>
                                {t('common.pagina')} {pagination.page} {t('common.de')} {pagination.totalPages}
                            </span>
                            <button
                                className={styles.pageBtn}
                                disabled={pagination.page >= pagination.totalPages}
                                onClick={() => handlePageChange(pagination.page + 1)}
                            >
                                {t('common.proxima')}
                            </button>
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
}
